import { useMemo, useState } from "react";
import SectionHeading from "./SectionHeading";
import { films, filmTags } from "@/mocks/films";

export default function FilmLibrary() {
  const [activeTag, setActiveTag] = useState("全部");
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return films.filter((film) => {
      const matchTag = activeTag === "全部" || film.tags.includes(activeTag);
      const matchKeyword =
        kw.length === 0 ||
        film.title.toLowerCase().includes(kw) ||
        film.en.toLowerCase().includes(kw) ||
        film.director.toLowerCase().includes(kw) ||
        film.year.includes(kw);
      return matchTag && matchKeyword;
    });
  }, [activeTag, keyword]);

  return (
    <section id="library" className="scroll-mt-20 bg-background-100 px-4 md:px-8 py-16 md:py-24">
      <div className="max-w-6xl mx-auto">
        <SectionHeading
          eyebrow="Film Library"
          title="经典港片与怀旧片影片库"
          desc="以下条目涵盖武侠、警匪、喜剧、文艺与爱情等类型。你可以用类型标签筛选，或直接搜索片名、导演与年份，快速定位想看的作品。"
        />

        <div className="mt-8 md:mt-12 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
          <div className="flex flex-wrap gap-2 flex-1">
            {filmTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setActiveTag(tag)}
                className={`px-3.5 py-2 rounded-full text-sm font-label whitespace-nowrap transition-colors ${
                  activeTag === tag
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-72">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索片名 / 导演 / 年份"
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
            />
          </div>
        </div>

        <p className="mt-4 text-xs md:text-sm text-foreground-500 font-label">
          共 {filtered.length} 条结果
          {activeTag !== "全部" ? ` · 类型「${activeTag}」` : ""}
          {keyword.trim() ? ` · 关键词「${keyword.trim()}」` : ""}
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {filtered.map((film) => (
              <article
                key={film.id}
                className="group flex flex-col sm:flex-row rounded-lg overflow-hidden border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              >
                <div className="relative w-full sm:w-36 shrink-0 aspect-[3/4] sm:aspect-auto sm:min-h-[196px] overflow-hidden">
                  <img
                    src={film.poster}
                    alt={`${film.title} 影片封面`}
                    className="w-full h-full object-cover object-top"
                  />
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-primary-950/75 text-accent-100 text-[11px] font-label">
                    {film.year}
                  </span>
                </div>
                <div className="flex flex-col flex-1 p-4 md:p-5">
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">
                      {film.title}
                    </h3>
                  </div>
                  <p className="mt-0.5 text-xs text-foreground-400">{film.en}</p>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600 flex-1">
                    {film.summary}
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {film.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-label"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-background-200 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500 font-label">
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-user-star-line" />
                      {film.director}
                    </span>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-time-line" />
                      {film.duration}
                    </span>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-emotion-line" />
                      {film.mood}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-search-line text-2xl" />
            </span>
            <p className="mt-4 font-heading text-base text-foreground-800">
              没有找到符合条件的影片
            </p>
            <p className="mt-2 text-sm text-foreground-500">
              试试更换类型标签，或换一个关键词。
            </p>
            <button
              type="button"
              onClick={() => {
                setActiveTag("全部");
                setKeyword("");
              }}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-label whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}